import { getPagesContent, useAppSelector, useGetCompetitionLeaderboard } from '@pkg/hooks';

import { type ResourceId } from '@app/client';
import { entryToGameItemData, sortedEntries } from '@app/pages/FantasyWishlist/sections/LeaderboardSection/utilities';
import { useFantasyMetadataStore } from '@app/store/zustand';

interface UseLeaderboardItemsProps {
    query?: string;
    teamId?: ResourceId;
    disabled?: boolean;
    interval?: boolean;
    page?: number;
    limit?: number;
}
export function useLeaderboardItems(props?: UseLeaderboardItemsProps) {
    const { query, teamId, page, limit, interval, disabled } = props || {};
    const { currentGame } = useFantasyMetadataStore();

    const userId = useAppSelector((state) => state.user?.userAccount?.user.id);
    const {
        data: leaderboardData,
        isLoading,
        fetchNextPage,
        hasNextPage,
    } = useGetCompetitionLeaderboard({
        query,
        teamId,
        page,
        limit,
        interval,
        ignoreSignal: true,
        slug: currentGame?.slug,
        disabled,
    });

    const entries = sortedEntries(getPagesContent(leaderboardData?.pages) || []);
    const playerItems = entries?.map((item) => entryToGameItemData(item, currentGame?.max_value || 100000));
    const leaderboardPlayerItem = playerItems?.find(({ user_id }) => user_id === userId);

    const maxRank = leaderboardData?.pages?.[0]?.paging?.total || 0;

    const disabledUser = !userId || !!leaderboardPlayerItem || isLoading || disabled;
    const { data: playerData, isLoading: isLoadingPlayerData } = useGetCompetitionLeaderboard({
        userId,
        interval,
        slug: currentGame?.slug,
        ignoreSignal: true,
        disabled: disabledUser,
    });

    const playerItem = userId ? getPagesContent(playerData?.pages)?.[0] || leaderboardPlayerItem : undefined;

    const isLoadingData = !disabled && (isLoading || (!disabledUser && isLoadingPlayerData));

    return {
        playerItem,
        maxRank,
        playerItems,
        fetchNextPage,
        hasNextPage,
        isLoading: isLoadingData,
    };
}
